import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Apollo } from 'apollo-angular';
import { KeycloakService } from 'keycloak-angular';
import { Observable, of, switchMap } from 'rxjs';
import { Tenant, TenantInput } from '../../entities/tenant/tenant';
import { ApolloService } from '../apollo.service';
import { UsersService } from '../users/users.service';
import * as fromGraphQl from './graphql';

/**
 * The users service.
 */
@Injectable()
export class TenantsService {
  public theme = 'standard';
  private renderer: Renderer2;
  constructor(
    private apolloService: ApolloService,
    private apollo: Apollo,
    private keycloakAngular: KeycloakService,
    rendererFactory: RendererFactory2,
    private userService: UsersService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }


  public getCurrentTenant(
    fragment: string = fromGraphQl.DEFAULT_TENANT_FRAGMENT,
    fragmentName: string = fromGraphQl.DEFAULT_TENANT_FRAGMENT_NAME
  ): Observable<Tenant> {
    let currentTenantId = this.userService.getCurrentTenantId();
    return this.apolloService
      .query<fromGraphQl.TenantQueryResponse>(fromGraphQl.getTenantQuery(
        fragment, fragmentName
      ),
        {
          id: currentTenantId
        })
      .pipe(
        switchMap((result) => {
          return of(new Tenant(result.data.tenant));
        })
      );
  }

   /**
   * creates a tenant
   * @param tenantInput user Input
   * @param callback method
   */
    public createTenant(tenantInput: TenantInput, callback: any): void {
      this.apolloService.mutateAndExecute(
        fromGraphQl.CREATE_TENANT_MUTATION,
        {
          input: tenantInput,
        },
        callback
      );
    }

     /**
   * updates a tenant
   * @param tenantInput user Input
   * @param callback method
   */
  public updateTenant(tenantInput: TenantInput, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_TENANT_MUTATION,
      {
        input: tenantInput,
      },
      callback
    );
  }

  /**
   * updates a tenant
   * @param tenantInput user Input
   * @param callback method
   */
  public updateInvoiceMail(id: string, invoiceMail: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_INVOICEMAIL_MUTATION,
      {
        id,
        invoiceMail
      },
      callback
    );
  }

  /**
   * updates the invoice address of a tenant
   * @param tenantInput user Input
   * @param callback method
   */
  public updateInvoiceAddress(input: any, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_INVOICE_ADDRESS_MUTATION,
      {
        input
      },
      callback
    );
  }

  public deleteInvoiceAddress(id: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.DELETE_INVOICE_ADDRESS_MUTATION,
      {
        id
      },
      callback
    );
  }

  public deleteLicenceRequests(tenantId: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.DELETE_LICENCE_REQUESTS_MUTATION,
      {
        tenantId
      },
      callback
    );
  }

  public deleteInvoiceEmail(id: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.DELETE_INVOICE_EMAIL_MUTATION,
      {
        id
      },
      callback
    );
  }

  /**
   * updates an user
   * @param tenant
   * returns boolean
   */
   public switchTenant(tenant: string): void {
    this.apollo
      .mutate({
        mutation: fromGraphQl.SWITCH_TENANT_MUTATION,
        variables: {
          tenant,
        },
      })
      .subscribe(
        ({ }) => {
          this.keycloakAngular.updateToken(-1).then(function(success): void {
            window.location.reload();
          });
        },
        (error) => {
          console.log('there was an error sending the query', error);
        }
      );
  }

  getVatTypes () {
    let types = [
      { id: 'CH_UID', name: 'CH UID' },
      { id: 'CH_VAT', name: 'CH VAT' },
      { id: 'DE_STN', name: 'DE STN' },
      { id: 'EU_VAT', name: 'EU VAT' }
    ];
    return types;
  }

  getVatTypeName(value: any): string {
    return this.getVatTypes().find((vatType) => vatType.id === value)?.name;
  }

  vatNumberValidator(): ValidatorFn {
    const vatAndTaxRegex = /^(DE\d{9}|[0-9]{11}|ATU\d{8}|... |CHE\d{9}(MWST)?)$/;

    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = vatAndTaxRegex.test(control.value);
      return isValid ? null : { invalidVatOrTax: true };
    };
  }
}
